import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    padding: 28,
    fontFamily: "Helvetica",
    fontSize: 11,
    color: "#1f2530",
  },
  header: {
    borderBottomWidth: 2,
    borderBottomColor: "#25374f",
    borderStyle: "solid", // Changed from borderBottomStyle
    paddingBottom: 10,
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    color: "#25374f",
    fontWeight: "bold",
  },
  date: {
    fontSize: 10,
    color: "#4b5563",
    marginTop: 4,
  },
  section: {
    marginBottom: 16,
    padding: 12,
    borderRadius: 6,
    borderWidth: 1, // Unified width
    borderColor: "#e5e7eb", // Unified color
    borderStyle: "solid", // Unified style (Fixes the crash)
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#25374f",
    marginBottom: 8,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  label: {
    color: "#4b5563",
  },
  value: {
    fontWeight: "bold",
  },
  recommendationBox: {
    backgroundColor: "#f2f6fb",
    borderWidth: 1, // Unified width
    borderColor: "#25374f", // Unified color
    borderStyle: "solid", // Unified style (Fixes the crash)
    padding: 12,
    borderRadius: 6,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#25374f",
    color: "white",
    padding: 6,
    fontWeight: "bold",
    fontSize: 10,
  },
  tableRow: {
    flexDirection: "row",
    padding: 6,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    borderStyle: "solid", // Changed from borderBottomStyle
  },
  colSmall: { width: "25%" },
  colMedium: { width: "35%" },
  colLarge: { width: "40%" },
  muted: {
    color: "#6b7280",
    fontSize: 10,
    marginTop: 4,
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 28,
    right: 28,
    fontSize: 9,
    color: "#6b7280",
    flexDirection: "row",
    justifyContent: "space-between",
  },
});

const formatNumber = (value, digits = 1) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return num.toFixed(digits);
};

const formatSeconds = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? `${num.toFixed(1)} s` : "—";
};

const sumArray = (values) => {
  if (!Array.isArray(values)) return null;
  let total = 0;
  let count = 0;

  values.forEach((current) => {
    if (Number.isFinite(current)) {
      total += current;
      count += 1;
    }
  });

  return count > 0 ? total : null;
};

const toObject = (value) => (value && typeof value === "object" ? value : null);
const toArray = (value) => (Array.isArray(value) ? value : []);

const getDisplayNumber = (value) => {
  if (value === null || value === undefined) return "—";
  const num = Number(value);
  if (Number.isFinite(num)) return num.toString();
  return "—";
};

const normalizeSimulationResult = (result) => {
  const response = toObject(result?.response);

  return {
    simulation: response ?? toObject(result) ?? {},
    floorMeta: toObject(result?.floorMeta) ?? {},
  };
};

const pickRecommendation = (simulation) => {
  if (!simulation) return null;
  const direct = simulation.recommendation;
  if (direct?.cibse_compliant) return direct;

  const candidates = Array.isArray(simulation.all_results)
    ? simulation.all_results.filter((item) => item?.cibse_compliant)
    : [];
  if (candidates.length) {
    return candidates
      .slice()
      .sort((a, b) => {
        if (Boolean(a?.bco_compliant) !== Boolean(b?.bco_compliant)) {
          return a?.bco_compliant ? -1 : 1;
        }
        if (Number.isFinite(a?.num_lifts) && Number.isFinite(b?.num_lifts)) {
          const diff = a.num_lifts - b.num_lifts;
          if (diff !== 0) return diff;
        }
        return (a?.performance?.avg_wait_time_s || Infinity) - (b?.performance?.avg_wait_time_s || Infinity);
      })[0];
  }

  return direct || null;
};

const buildHeatmapHighlights = (allResults) => {
  if (!Array.isArray(allResults)) return [];
  return allResults
    .filter((item) => Number.isFinite(item?.performance?.avg_wait_time_s))
    .sort((a, b) => a.performance.avg_wait_time_s - b.performance.avg_wait_time_s)
    .slice(0, 4)
    .map((item) => ({
      size: item.lift_size_key || "Option",
      lifts: Number.isFinite(item?.num_lifts) ? item.num_lifts.toString() : "—",
      wait: formatSeconds(item.performance.avg_wait_time_s),
      compliant: item.bco_compliant && item.cibse_compliant,
    }));
};

const buildPerformanceCurve = (allResults) => {
  if (!Array.isArray(allResults)) return [];
  const grouped = new Map();

  allResults.forEach((item) => {
    if (!Number.isFinite(item?.num_lifts) || !Number.isFinite(item?.performance?.avg_wait_time_s)) return;
    const prev = grouped.get(item.num_lifts);
    if (!prev || item.performance.avg_wait_time_s < prev.wait) {
      grouped.set(item.num_lifts, {
        lifts: item.num_lifts,
        wait: item.performance.avg_wait_time_s,
        size: item.lift_size_key || "Option",
        compliant: Boolean(item.bco_compliant && item.cibse_compliant),
      });
    }
  });

  return Array.from(grouped.values())
    .sort((a, b) => a.lifts - b.lifts)
    .slice(0, 6)
    .map((row) => ({ ...row, lifts: row.lifts.toString(), wait: formatSeconds(row.wait) }));
};

const LiftPDFDocument = ({ project, inputs, result }) => {
  const { simulation, floorMeta } = normalizeSimulationResult(result);
  const inputMeta = toObject(inputs) ?? {};
  const allResults = toArray(simulation?.all_results);

  const recommendation = pickRecommendation(simulation);
  const performance = toObject(recommendation?.performance) ?? {};
  const projectName = project?.name || "Pebbledash project";
  const buildingUse = project?.building_use || "—";
  const totalFloors = getDisplayNumber(
    inputMeta?.numFloors ?? floorMeta?.numFloors ?? project?.floors_count,
  );
  const totalArea = sumArray(toArray(inputMeta?.areas)) ?? sumArray(toArray(floorMeta?.areas));
  const entranceFloor = Number.isFinite(inputMeta?.entranceFloor)
    ? inputMeta.entranceFloor + 1
    : Number.isFinite(floorMeta?.entranceFloor)
      ? floorMeta.entranceFloor + 1
      : getDisplayNumber(project?.entrance_floor);

  const occupancy = Number.isFinite(inputMeta?.occupancy_ratio)
    ? inputMeta.occupancy_ratio
    : project?.occupancy_ratio;
  const floorHeights = toArray(inputMeta?.floor_heights).length
    ? toArray(inputMeta?.floor_heights)
    : toArray(floorMeta?.floor_heights);
  const floorHeightsTotal = sumArray(floorHeights);
  const avgFloorHeight =
    Number.isFinite(floorHeightsTotal) && floorHeights.length > 0
      ? formatNumber(floorHeightsTotal / floorHeights.length, 2)
      : null;

  const heatmapHighlights = Array.isArray(allResults) ? buildHeatmapHighlights(allResults) : [];
  const curveRows = Array.isArray(allResults) ? buildPerformanceCurve(allResults) : [];

  // Sanity check data before rendering
  const safeProjectName = typeof projectName === "string" ? projectName : "Pebbledash project";
  const safeBuildingUse = typeof buildingUse === "string" ? buildingUse : "—";
  const safeTotalFloors = typeof totalFloors === "string" ? totalFloors : getDisplayNumber(totalFloors);
  const safeTotalArea = Number.isFinite(totalArea) ? `${formatNumber(totalArea, 0)} m²` : "—";
  const safeEntranceFloor = Number.isFinite(entranceFloor)
    ? `Level ${Number(entranceFloor)}`
    : "—";
  const safeOccupancy = Number.isFinite(occupancy) ? `1:${formatNumber(occupancy, 0)} m²` : "—";
  const safeAvgFloorHeight = avgFloorHeight ? `${avgFloorHeight} m` : "—";

  const safeNumLifts = Number.isFinite(recommendation?.num_lifts)
    ? String(recommendation.num_lifts)
    : "—";
  const safeCapacity = typeof recommendation?.lift_size_key === "string" ? recommendation.lift_size_key : "—";
  const safeSpeed = Number.isFinite(recommendation?.speed_mps)
    ? `${formatNumber(recommendation.speed_mps, 1)} m/s`
    : "—";
  const safeCarDimensions =
    Number.isFinite(recommendation?.car_width_mm) && Number.isFinite(recommendation?.car_depth_mm)
      ? `${formatNumber(recommendation.car_width_mm, 0)} x ${formatNumber(recommendation.car_depth_mm, 0)} mm`
      : "—";
  const safeCompliance = recommendation?.bco_compliant && recommendation?.cibse_compliant
    ? "BCO & CIBSE compliant"
    : recommendation?.cibse_compliant
      ? "CIBSE compliant"
      : recommendation?.num_lifts
        ? "Review performance"
        : "Awaiting simulation";

  const safeHighRiseStart = Number.isFinite(simulation?.high_rise_start_floor)
    ? `Level ${simulation.high_rise_start_floor + 1}`
    : "—";
  const safeDropoffFloor = Number.isFinite(simulation?.dropoff_floor)
    ? `Level ${simulation.dropoff_floor + 1}`
    : "—";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.title}>Pebbledash Lift Analysis Report</Text>
          <Text style={styles.date}>{new Date().toLocaleDateString()}</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Project summary</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Project</Text>
            <Text style={styles.value}>{safeProjectName}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Building use</Text>
            <Text style={styles.value}>{safeBuildingUse}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Total floors</Text>
            <Text style={styles.value}>{safeTotalFloors}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Net internal area</Text>
            <Text style={styles.value}>{safeTotalArea}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Entrance level</Text>
            <Text style={styles.value}>{safeEntranceFloor}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Occupancy ratio</Text>
            <Text style={styles.value}>{safeOccupancy}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Avg floor height</Text>
            <Text style={styles.value}>{safeAvgFloorHeight}</Text>
          </View>
        </View>

        <View style={[styles.section, styles.recommendationBox]}>
          <Text style={styles.sectionTitle}>Recommended configuration</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Number of lifts</Text>
            <Text style={styles.value}>{safeNumLifts}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Capacity</Text>
            <Text style={styles.value}>{safeCapacity}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Speed</Text>
            <Text style={styles.value}>{safeSpeed}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Car dimensions</Text>
            <Text style={styles.value}>{safeCarDimensions}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Compliance</Text>
            <Text style={styles.value}>{safeCompliance}</Text>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Performance metrics</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Average wait time</Text>
            <Text style={styles.value}>{formatSeconds(performance?.avg_wait_time_s)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Time to destination</Text>
            <Text style={styles.value}>{formatSeconds(performance?.avg_time_to_dest_s)}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Handling capacity</Text>
            <Text style={styles.value}>
              {Number.isFinite(performance?.handling_capacity_pct)
                ? `${formatNumber(performance.handling_capacity_pct, 1)} %`
                : "—"}
            </Text>
          </View>
          <Text style={styles.muted}>
            Includes summary of the solution heatmap, performance curve, and zoning strategy
            generated by Pebbledash.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Heat map highlights</Text>
          {heatmapHighlights.length === 0 ? (
            <Text style={styles.muted}>Run the simulation to populate heat map results.</Text>
          ) : (
            <View>
              <View style={styles.tableHeader}>
                <Text style={styles.colLarge}>Lift size</Text>
                <Text style={styles.colSmall}>Lifts</Text>
                <Text style={styles.colMedium}>Avg wait</Text>
                <Text style={styles.colSmall}>Compliant</Text>
              </View>
              {heatmapHighlights.map((row, index) => (
                <View key={`${row.size}-${index}`} style={styles.tableRow}>
                  <Text style={styles.colLarge}>{row.size}</Text>
                  <Text style={styles.colSmall}>{row.lifts}</Text>
                  <Text style={styles.colMedium}>{row.wait}</Text>
                  <Text style={styles.colSmall}>{row.compliant ? "Yes" : "Review"}</Text>
                </View>
              ))}
            </View>
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Performance curve</Text>
          {curveRows.length === 0 ? (
            <Text style={styles.muted}>Run the simulation to see curve checkpoints.</Text>
          ) : (
            <View>
              <View style={styles.tableHeader}>
                <Text style={styles.colSmall}>Lifts</Text>
                <Text style={styles.colMedium}>Avg wait</Text>
                <Text style={styles.colLarge}>Preferred size</Text>
                <Text style={styles.colSmall}>Compliant</Text>
              </View>
              {curveRows.map((row) => (
                <View key={row.lifts} style={styles.tableRow}>
                  <Text style={styles.colSmall}>{row.lifts}</Text>
                  <Text style={styles.colMedium}>{row.wait}</Text>
                  <Text style={styles.colLarge}>{row.size}</Text>
                  <Text style={styles.colSmall}>{row.compliant ? "Yes" : "Review"}</Text>
                </View>
              ))}
            </View>
          )}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Zoning strategy</Text>
          <View style={styles.row}>
            <Text style={styles.label}>High-rise starts</Text>
            <Text style={styles.value}>{safeHighRiseStart}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Transfer / drop-off</Text>
            <Text style={styles.value}>{safeDropoffFloor}</Text>
          </View>
          <Text style={styles.muted}>
            Zoning recommendations are derived from the simulated traffic profile and entrance floor
            provided in the calculator.
          </Text>
        </View>

        <View style={styles.footer} fixed>
          <Text>Generated by Pebbledash</Text>
          <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
};

export default LiftPDFDocument;
